import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  // YAxis,
} from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../config/enum';
import currentTimeZone from '../../../config/timeZone';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import CustomInputSwitch from '../../../shared-components/custom-input-switch/CustomInputSwitch';
import CustomToggleButton from '../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../shared-functions/ChartFunctions';
import dashboard from '../../../utils/api/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Station alert chart component
 * @returns
 */
export default function StationAlertsChart() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');

  const [barChartLabel, setBarChartLabel] = useState([
    { name: 'maintenance', value: 0, color: '#0088FE', isActive: true },
    { name: 'transaction', value: 0, color: '#00C49F', isActive: true },
    // { name: 'energy', value: 0, color: '#FFBB28', isActive: true },
    { name: 'performance', value: 0, color: '#FF8042', isActive: true },
  ]);

  /**
   * Show & hide data in bar-chart label
   */
  const handleToggleLabelActive = (name: string, value: boolean) => {
    setBarChartLabel((previousValue) =>
      previousValue.map((item) => (item.name === name ? { ...item, isActive: value } : item)),
    );
  };

  /**
   * Update all alerts totals
   */
  const updateAllAlertTotals = (data: any[]) => {
    // Initialize totals
    const totals = {
      maintenance: 0,
      transaction: 0,
      performance: 0,
    };

    // Accumulate totals
    data.forEach((item) => {
      totals.maintenance += item.maintenanceAlert || 0;
      totals.transaction += item.transactionAlert || 0;
      totals.performance += item.performanceAlert || 0;
    });

    // Update state
    setBarChartLabel((previousData) =>
      previousData.map((label) => ({
        ...label,
        value: totals[label.name as keyof typeof totals] || 0,
      })),
    );
  };

  /**
   * Function to show custom tooltip
   * @param param0
   * @returns
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];
      return (
        <div className="station-performance-custom-tooltip">
          <p className="label">{data?.payload?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#0088FE' }}>
              {t('maintenance')}: {data?.payload?.maintenance}
            </p>
            <p style={{ color: '#00C49F' }}>
              {t('transaction')}: {data?.payload?.transaction}
            </p>
            <p style={{ color: '#FF8042' }}>
              {t('performance')}: {data?.payload?.performance}
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Fetch power consumed data based on time range
   * @param range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    try {
      setIsLoading(true);
      const response = await dashboard.getAlertsReport({
        timePeriod: range,
        timeZone: currentTimeZone,
      });

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== range) return;

      // ✅ Otherwise, update the chart with the right data
      updateAllAlertTotals(response?.data);

      if (range === 'last12months' && response.status === 200) {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="station-alert-chart-wrap">
      <div className="station-alert-chart-header">
        <div className="header-title">
          <h6>{t('stationAlert')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/alerts/all')}>
            <NavigateArrow />
          </span>
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => t(duration.label))}
          onToggle={(index, label) => {
            const selectedDuration = chartTimePeriod.find(
              (duration) => t(duration.label) === label,
            );
            fetchPowerConsumedReport(selectedDuration?.value || 'last24hours');
            setIsLoading(true);
          }}
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      {isLoading ? (
        <center className="station-alert-loader">
          <BatteryLoader />
        </center>
      ) : (
        <div className="station-alert-chart-container">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barGraphData}
              margin={{
                top: 50,
                right: 0,
                left: -10,
                bottom: 5,
              }}>
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 12 }} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
              {/* <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12 }} /> */}
              {barChartLabel
                .filter((label) => label.isActive)
                .map((label) => (
                  <Bar
                    key={label.name}
                    dataKey={`${label.name}Alert`} // e.g., 'performanceAlert'
                    fill={label.color}
                  />
                ))}
            </BarChart>
          </ResponsiveContainer>
          <div className="station-alert-chart-label">
            {barChartLabel.map((eachData) => (
              <div className="each-label-container">
                <div
                  className="each-label"
                  style={{ '--label-color': eachData.color } as React.CSSProperties}>
                  <p>{t(eachData.name)}</p>
                  <div className="each-label-value">
                    <span>{eachData.value}</span>
                  </div>
                </div>
                <CustomInputSwitch
                  name={eachData.name}
                  value={eachData.isActive}
                  onChangeValue={handleToggleLabelActive}
                  fontSize="small"
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
